import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import BrandMark from "./BrandMark";
import LanguageSwitcher from "./LanguageSwitcher";
import { useFlags } from "../lib/flags";

const NAV_KEYS = [
    { to: "/destinations", key: "destinations", flag: "destinations" },
    { to: "/experiences", key: "experiences", flag: "experiences" },
    { to: "/parcours", key: "parcours", flag: "parcours" },
    { to: "/adresses", key: "adresses", flag: "adresses" },
    { to: "/evenements", key: "agenda", flag: "agenda" },
    { to: "/balades", key: "balades", flag: "balades" },
    { to: "/galerie", key: "galerie", flag: "galerie" },
    { to: "/magazine", key: "magazine", flag: "magazine" },
    { to: "/loger", key: "loger", flag: "loger" },
    { to: "/carte", key: "carte" },
];

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const { flags } = useFlags();
    const { t, i18n } = useTranslation();

    const navItems = NAV_KEYS.filter((i) => !i.flag || flags[i.flag] !== false);
    const showCoteSecrete = flags["cote-secrete"] !== false;
    const showClub = flags.club !== false;
    const showFestival = flags.festival !== false;

    const dateLocale = (i18n.language || "fr").startsWith("en") ? "en-GB" : "fr-FR";
    const today = new Date().toLocaleDateString(dateLocale, { day: "2-digit", month: "long", year: "numeric" });

    const darkHeroPaths = ["/", "/festival"];
    const isDarkHeroPath = darkHeroPaths.includes(location.pathname) ||
        location.pathname.startsWith("/destinations/") ||
        location.pathname.startsWith("/adresses/") ||
        location.pathname.startsWith("/evenements/") ||
        location.pathname.startsWith("/balades/");

    const lightMode = !scrolled && isDarkHeroPath;

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 80);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [location.pathname]);

    return (
        <>
            {/* ============ TOP EDITORIAL BAR ============ */}
            <div className={`fixed top-0 left-0 right-0 z-40 border-b transition-all duration-500 ${
                lightMode ? "bg-transparent border-transparent text-cp-ivoire" : scrolled ? "bg-cp-ivoire/95 backdrop-blur-xl border-cp-border" : "bg-cp-ivoire border-cp-border"
            }`} data-testid="top-bar">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-10 flex items-center justify-between">
                    <p className={`cp-tiny ${lightMode ? "text-cp-ivoire/80" : "text-cp-gris"}`}>
                        {t("header.issue")} · {today}
                    </p>
                    <div className={`cp-tiny hidden md:flex items-center gap-5 ${lightMode ? "text-cp-ivoire/80" : "text-cp-gris"}`}>
                        <Link to="/manifeste" className={`${lightMode ? "hover:text-cp-sable" : "hover:text-cp-foret"} transition-colors`} data-testid="top-bar-manifeste">
                            {t("nav.manifeste")}
                        </Link>
                        {showCoteSecrete && (<>
                            <span className="opacity-30">·</span>
                            <Link to="/cote-secrete" className={`${lightMode ? "hover:text-cp-sable" : "hover:text-cp-foret"} transition-colors italic`} data-testid="top-bar-cote-secrete">
                                {t("nav.coteSecrete")}
                            </Link>
                        </>)}
                        <span className="opacity-30">·</span>
                        <span>50°03'N 1°22'E</span>
                    </div>
                    <div className="flex items-center gap-4">
                        {showFestival ? (
                            <Link to="/festival" className={`cp-tiny ${lightMode ? "text-cp-sable" : "text-cp-foret"}`} data-testid="top-bar-festival">
                                {t("header.preparingFestival")}
                            </Link>
                        ) : null}
                        <LanguageSwitcher light={lightMode} />
                    </div>
                </div>
            </div>

            {/* ============ MAIN HEADER ============ */}
            <header
                data-testid="site-header"
                className={`fixed top-10 left-0 right-0 z-40 transition-all duration-500 ${
                    lightMode
                        ? "bg-transparent"
                        : scrolled
                            ? "bg-cp-ivoire/90 backdrop-blur-xl border-b border-cp-border"
                            : "bg-cp-ivoire border-b border-cp-border"
                }`}
            >
                <div className="max-w-[1600px] mx-auto px-5 md:px-8 xl:px-12 flex items-center justify-between gap-4 h-16 md:h-20">
                    <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group shrink-0 min-w-0">
                        <BrandMark
                            size={36}
                            color={lightMode ? "var(--cp-ivoire)" : "var(--cp-encre)"}
                            tagline
                            testid="header-brandmark"
                        />
                    </Link>

                    <nav className="hidden xl:flex items-center gap-2 2xl:gap-4 shrink-0" data-testid="main-nav">
                        {navItems.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                data-testid={`nav-${item.to.replace("/", "")}`}
                                className={({ isActive }) =>
                                    `cp-tiny text-[10px] 2xl:text-[11px] whitespace-nowrap transition-colors duration-300 ${
                                        isActive
                                            ? lightMode ? "text-cp-sable" : "text-cp-foret"
                                            : lightMode ? "text-cp-ivoire hover:text-cp-sable" : "text-cp-encre hover:text-cp-foret"
                                    }`
                                }
                            >
                                {t(`nav.${item.key}`)}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3 md:gap-5 shrink-0">
                        {showClub && (
                            <Link
                                to="/club"
                                data-testid="cta-header-club"
                                className={`cp-tiny hidden 2xl:inline-block px-4 py-2 border whitespace-nowrap transition-colors ${lightMode ? "text-cp-encre bg-cp-sable border-cp-sable hover:bg-cp-ivoire" : "text-cp-ivoire bg-cp-foret border-cp-foret hover:bg-cp-encre hover:border-cp-encre"}`}
                            >
                                {t("nav.club")}
                            </Link>
                        )}
                        <button
                            className={`xl:hidden ${lightMode ? "text-cp-ivoire" : "text-cp-encre"}`}
                            aria-label="menu"
                            onClick={() => setOpen((v) => !v)}
                            data-testid="menu-toggle"
                        >
                            {open ? <X size={22} strokeWidth={1.25} /> : <Menu size={22} strokeWidth={1.25} />}
                        </button>
                    </div>
                </div>

                {open && (
                    <div className="xl:hidden bg-cp-ivoire border-t border-cp-border" data-testid="mobile-nav">
                        <div className="px-6 py-8 flex flex-col gap-6">
                            {navItems.map((item) => (
                                <NavLink key={item.to} to={item.to} className="cp-serif text-3xl text-cp-encre">
                                    <em className="cp-display-italic">{t(`nav.${item.key}`)}</em>
                                </NavLink>
                            ))}
                            <Link to="/scene" className="cp-serif text-3xl text-cp-encre"><em className="cp-display-italic">{t("nav.scene")}</em></Link>
                            <Link to="/week-end" className="cp-btn-primary mt-4 justify-center">
                                {t("nav.weekend")}
                            </Link>
                        </div>
                    </div>
                )}
            </header>
        </>
    );
}
